import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Phone, Mail, AlertTriangle, Calendar, Shield, Check, Trash2 } from 'lucide-react';
import api from '../../services/api';

const NOTIFICATION_ICONS = {
  incoming_call: Phone,
  missed_call: Phone,
  voicemail: Phone,
  new_email: Mail,
  planchette_intel_update: Shield,
  calendar_reminder: Calendar,
  task_due: AlertTriangle,
  case_update: AlertTriangle,
};

const NOTIFICATION_COLORS = {
  incoming_call: 'text-red-400 bg-red-900/30',
  missed_call: 'text-orange-400 bg-orange-900/30',
  voicemail: 'text-yellow-400 bg-yellow-900/30',
  new_email: 'text-blue-400 bg-blue-900/30',
  planchette_intel_update: 'text-purple-400 bg-purple-900/30',
  calendar_reminder: 'text-green-400 bg-green-900/30',
  task_due: 'text-amber-400 bg-amber-900/30',
  case_update: 'text-teal-400 bg-teal-900/30',
};

const NOTIFICATION_ROUTES = {
  incoming_call: '/voip',
  missed_call: '/voip',
  voicemail: '/voip',
  new_email: '/mail',
  planchette_intel_update: '/planchette-intel',
  calendar_reminder: '/calendar',
  task_due: '/cases',
  case_update: '/cases',
};

const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await api.get('/presence/notifications');
      const raw = resp.data?.notifications ?? resp.data;
      const data = Array.isArray(raw) ? raw : [];
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (err) {
      // Endpoint may return non-200 — silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Listen for WebSocket notification events
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.type) {
        fetchNotifications();
      }
    };
    window.addEventListener('virgo-notification', handler);
    return () => window.removeEventListener('virgo-notification', handler);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await api.put(`/api/v1/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/api/v1/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/api/v1/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const wasUnread = notifications.find(n => n.id === id && !n.read);
        return wasUnread ? Math.max(0, prev - 1) : prev;
      });
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const route = NOTIFICATION_ROUTES[notification.type] || '/';
    if (notification.action_url) {
      navigate(notification.action_url);
    } else {
      navigate(route);
    }
    setOpen(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-virgo-navy-700 transition-colors text-virgo-teal-300 hover:text-white"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Slide-out Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[80vh] bg-virgo-navy-800 border border-virgo-teal-900 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-virgo-teal-900">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-virgo-teal-400 hover:text-virgo-teal-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-virgo-teal-400 text-sm">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-virgo-teal-500">
                <Bell size={32} className="mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || AlertTriangle;
                const colorClass = NOTIFICATION_COLORS[notification.type] || 'text-gray-400 bg-gray-900/30';
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-virgo-navy-700 cursor-pointer transition-colors border-b border-virgo-navy-700 ${
                      !notification.read ? 'bg-virgo-navy-750' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'text-white font-medium' : 'text-virgo-teal-200'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-virgo-teal-400 rounded-full flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      {notification.body && (
                        <p className="text-xs text-virgo-teal-400 mt-0.5 truncate">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-virgo-teal-600 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="p-1 text-virgo-teal-500 hover:text-virgo-teal-300 transition-colors"
                          title="Mark as read"
                        >
                          <Check size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-1 text-virgo-teal-600 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
